import React, { useMemo, useState } from 'react';
import { BellIcon, LockIcon } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionHeading } from '../SectionHeading';

// STORE CLOSED UNTIL LAUNCH (Ryan, Aug 30). The Shopify catalogue is fully
// drafted, so every /products/<handle> URL 404s and the storefront is empty.
// The tiles below are NON-CLICKABLE previews, not links. Do NOT re-add the
// product anchor or the SHOP ASTROSWAG button until the store is live again.
// See FAR_Store_Disabled_Until_Launch.md for the restore list.
//
// Deliberately absent: Racing Cap, Racing Iron-On Patch, Distressed Metal Dad
// Hat, the three [DUPLICATE] products, and the Future Astronaut Textured Hoodie.
//
// All 33 restore-list products now have a Cloudinary shop card, so all 33 have
// a tile. Champion hoodie, MagSafe case and FA AirPods case were the last built.
//
// CONTRAST (Ryan, Aug 31): the catalogue is mostly black product, and a black
// hoodie disappeared on the old near-black tile. First attempt used a violet
// backlight glow behind each silhouette -- Ryan rejected it, it read as an
// outline. The fix is a LIGHTER TILE GROUND instead: a mid-violet studio
// backdrop, no glow, just a soft contact shadow. The 1200x1200 far-card-*
// product cards in Shopify use the exact same ground so the two match.
// Do NOT re-add a drop-shadow glow filter here.
//
// WARNING: DO NOT point the Champion hoodie at `far-shop-texhoodie`. That card
// shows the Future Astronaut Textured Hoodie, which Ryan unlisted on Aug 30.
// Both are the same Champion blank but the chest artwork differs (the kept one
// prints the wordmark three times, stacked); reusing the card would advertise
// the wrong print and a colourway that is no longer for sale.
//
// Handles and prices verified against Shopify on 2026-08-31 so this list can go
// straight back to links when the store reopens.

const CLD = (key: string) => `https://res.cloudinary.com/kn1rv77n/image/upload/e_background_removal/f_auto,q_auto,w_320/far-shop-${key}.png`;

// Mid-violet studio backdrop, matching the far-card-* product cards:
// radial rgb(124,104,190) centre -> rgb(52,40,98) edge.
const TILE_BG = 'radial-gradient(118% 100% at 50% 36%, #7c68be 0%, #6553a6 42%, #43336e 74%, #342862 100%)';
type Cat = 'all' | 'apparel' | 'hats' | 'acc';
const TABS: {
  key: Cat;
  label: string;
  dot?: string;
}[] = [{
  key: 'all',
  label: 'ALL'
}, {
  key: 'apparel',
  label: 'APPAREL',
  dot: '#8b5cff'
}, {
  key: 'hats',
  label: 'HATS',
  dot: '#37e5ff'
}, {
  key: 'acc',
  label: 'ACCESSORIES',
  dot: '#c86bff'
}];
const PRODUCTS = [{
  title: 'Basic Hoodie',
  price: '$66',
  handle: 'basic-hoodie',
  cat: 'apparel',
  key: 'hoodie'
}, {
  title: 'Champion Tie-Dye Hoodie',
  price: '$70',
  handle: 'unisex-champion-tie-dye-hoodie-1',
  cat: 'apparel',
  key: 'champhoodie'
}, {
  title: 'Baseball Jersey Black',
  price: '$72',
  handle: 'baseball-jersey-black',
  cat: 'apparel',
  key: 'jerseyb'
}, {
  title: 'Baseball Jersey White',
  price: '$72',
  handle: 'baseball-jersey-white',
  cat: 'apparel',
  key: 'jerseyw'
}, {
  title: 'Faded Tee',
  price: '$40',
  handle: 'oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'fadedtee'
}, {
  title: 'Tie-Dye Tee',
  price: '$40',
  handle: 'oversized-tie-dye-t-shirt',
  cat: 'apparel',
  key: 'tiedyetee'
}, {
  title: 'Alien Reflection Tee',
  price: '$36',
  handle: 'alien-reflection-oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'alientee'
}, {
  title: 'Performance Tee',
  price: '$29',
  handle: 'unisex-performance-crew-neck-t-shirt',
  cat: 'apparel',
  key: 'perftee'
}, {
  title: 'Rocket Dad Hat',
  price: '$30',
  handle: 'dad-hat',
  cat: 'hats',
  key: 'rocketdad'
}, {
  title: 'OG Dad Hat',
  price: '$30',
  handle: 'og-logo-dad-hat',
  cat: 'hats',
  key: 'ogdad'
}, {
  title: 'FAR Snapback',
  price: '$30',
  handle: 'flat-bill-cap-1',
  cat: 'hats',
  key: 'snapback'
}, {
  title: 'Flat Bill Cap',
  price: '$28',
  handle: 'flat-bill-cap',
  cat: 'hats',
  key: 'flatbill'
}, {
  title: 'Vintage Cap',
  price: '$30',
  handle: 'vintage-cap',
  cat: 'hats',
  key: 'vintage'
}, {
  title: 'FAR Rope Cap',
  price: '$25',
  handle: 'future-astronaut-cap-black',
  cat: 'hats',
  key: 'ropecap'
}, {
  title: 'Rocket Beanie',
  price: '$30',
  handle: 'embroidered-beanie',
  cat: 'hats',
  key: 'rocketbeanie'
}, {
  title: 'OG Beanie',
  price: '$30',
  handle: 'og-logo-beanie',
  cat: 'hats',
  key: 'ogbeanie'
}, {
  title: 'AOP Bucket Hat',
  price: '$32',
  handle: 'bucket-hat-aop',
  cat: 'hats',
  key: 'aopbucket'
}, {
  title: 'Astronaut Bucket Hat',
  price: '$32',
  handle: 'astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'astrobucket'
}, {
  title: 'Alien Bucket Hat',
  price: '$32',
  handle: 'alien-pattern-bucket-hat',
  cat: 'hats',
  key: 'alienbucket'
}, {
  title: 'Alien Party Bucket Hat',
  price: '$25',
  handle: 'copy-of-astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'partybucket'
}, {
  title: 'Space Bandana',
  price: '$25',
  handle: 'all-over-print-bandana',
  cat: 'acc',
  key: 'bandana'
}, {
  title: 'Fanny Pack',
  price: '$44',
  handle: 'fanny-pack',
  cat: 'acc',
  key: 'fanny'
}, {
  title: 'Drawstring Bag',
  price: '$20',
  handle: 'drawstring-bag',
  cat: 'acc',
  key: 'drawstring'
}, {
  title: 'Astrology Shawl',
  price: '$62',
  handle: 'astrology-shawl',
  cat: 'acc',
  key: 'shawl'
}, {
  title: 'Astrology Scarf',
  price: '$35',
  handle: 'astrology-scarf',
  cat: 'acc',
  key: 'astroscarf'
}, {
  title: 'Galaxy Scarf',
  price: '$35',
  handle: 'galaxy-scarf',
  cat: 'acc',
  key: 'galaxyscarf'
}, {
  title: 'Space Pashmina',
  price: '$35',
  handle: 'space-scarf',
  cat: 'acc',
  key: 'pashmina'
}, {
  title: 'Space Scarf',
  price: '$40',
  handle: 'space-scarf-2',
  cat: 'acc',
  key: 'spacescarf2'
}, {
  title: 'Apple Watch Band',
  price: '$49.80',
  handle: 'watch-band-for-apple-watch',
  cat: 'acc',
  key: 'watchband'
}, {
  title: 'Rocket AirPods Case',
  price: '$31.50',
  handle: 'rocket-logo-airpods-case',
  cat: 'acc',
  key: 'airpods'
}, {
  title: 'FA AirPods Case',
  price: '$31.50',
  handle: 'future-astronaut-airpods-case',
  cat: 'acc',
  key: 'faairpods'
}, {
  title: 'Rocket iPhone Case',
  price: '$16',
  handle: 'rocket-logo-iphone-case',
  cat: 'acc',
  key: 'iphonecase'
}, {
  title: 'MagSafe Clear Case',
  price: '$25',
  handle: 'magsafe-clear-case-for-iphone',
  cat: 'acc',
  key: 'magsafe'
}];
export function AstroSwag() {
  const [cat, setCat] = useState<Cat>('all');
  const shown = useMemo(() => PRODUCTS.filter(p => cat === 'all' || p.cat === cat), [cat]);
  return <section id="astroswag" className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-id="element-82" data-mp="cvow8mkv51um">
      <SectionHeading eyebrow="MERCH" title={<>
            Future <span className="text-gradient" data-id="element-84" data-mp="lwd8bbqp4s4v">AstroSwag</span>
          </>} description="Shop's shut while we finish the first drop. Here's the whole rack." data-id="element-83" data-mp="7xdiw1vd097n" />

      <Reveal data-id="element-85" data-mp="hsxgqtthhhz1">
        <div className="mt-7 rounded-2xl bg-gradient-to-r from-[#37e5ff55] via-[#8b5cff55] to-[#c86bff55] p-px shadow-[0_0_44px_rgba(139,92,255,0.14)]" data-id="element-86" data-mp="2no055tx9rh99">
          <div className="overflow-hidden rounded-[calc(1rem-1px)] bg-[#07051a]/95" data-id="element-87" data-mp="2jnzdh7l5t9k9">
            <div className="flex flex-wrap items-center gap-1.5 border-b border-white/10 px-3 py-2.5 sm:px-4 sm:py-3" data-id="element-88" data-mp="29jj1ipaswclq">
              {TABS.map(t => <button key={t.key} type="button" onClick={() => setCat(t.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-tech text-[0.56rem] font-semibold tracking-[0.12em] transition-colors duration-200 sm:text-[0.6rem] ${cat === t.key ? 'border-transparent bg-gradient-to-r from-[#37e5ff] via-[#8b5cff] to-[#c86bff] text-[#050414] shadow-neon' : 'border-white/15 text-white/60 hover:border-violet/50 hover:text-white'}`} data-id="element-89" data-mp="1zso9t5escksw">
                  {t.dot && cat !== t.key && <span className="h-1.5 w-1.5 rounded-full" style={{
                background: t.dot
              }} aria-hidden="true" data-id="element-90" data-mp="3b5z6s2c7uxt0" />}
                  {t.label}
                </button>)}
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/[0.07] px-3 py-1 font-tech text-[0.52rem] font-semibold tracking-[0.14em] text-cyan/90 sm:text-[0.56rem]" data-id="element-91" data-mp="snsgnt8d7ng9">
                <LockIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-92" data-mp="ev2unosu6ij5" />
                OPENS JANUARY 2027
              </span>
            </div>

            <div className="scp-list grid max-h-[47vh] grid-cols-3 gap-2 overflow-y-auto overscroll-contain p-2.5 lg:max-h-[48vh] lg:grid-cols-6 lg:gap-2.5 lg:p-3.5" data-id="element-93" data-mp="9x1b72ri0h75">
              {shown.map(p => <div key={p.handle} className="flex cursor-default flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0a0720]" data-id="element-94" data-mp="3w2y99v9rky7b">
                  <span className="block aspect-square overflow-hidden" style={{
                background: TILE_BG
              }} data-id="element-95" data-mp="1uarwu310jws2">
                    <img src={CLD(p.key)} alt={p.title} loading="lazy" className="h-full w-full object-contain p-2.5 drop-shadow-[0_10px_12px_rgba(20,12,48,0.55)]" data-id="element-96" data-mp="1qahoijpr0ua1" />
                  </span>
                  <span className="px-2 pt-1.5 font-display text-[0.46rem] font-semibold uppercase leading-tight tracking-[0.04em] text-white lg:text-[0.5rem]" data-id="element-97" data-mp="3hxxwnzfmifhw">
                    {p.title}
                  </span>
                  <span className="px-2 pb-1.5 pt-0.5 font-tech text-[0.52rem] text-cyan lg:text-[0.56rem]" data-id="element-98" data-mp="2pato5khbnj1d">{p.price}</span>
                </div>)}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15} data-id="element-99" data-mp="33804z9ihj7ny">
        <div className="mt-5 flex flex-col items-center gap-2 lg:mt-6" data-id="element-100" data-mp="339b6t3sde5cf">
          <a href="#contact" className="group inline-flex items-center gap-2 rounded-full bg-violet px-7 py-3.5 font-tech text-xs font-semibold tracking-[0.16em] text-white shadow-neon transition-[transform,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft" data-id="element-101" data-mp="dj6g63n19k3v">
            <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-102" data-mp="2b77z270x6bd2" />
            TELL ME WHEN IT DROPS
          </a>
          <p className="font-script text-[0.95rem] text-magenta" data-id="element-103" data-mp="2g0ay2uogud0n">Store opens with the first releases</p>
        </div>
      </Reveal>
    </section>;
}